import { useCallback, useEffect, useState } from 'react';
import { CompanyPage, CompanyPageCursor, companiesService } from '../services/firebaseFunctions';

const emptyPage: CompanyPage = { items: [], nextCursor: null, totalCount: 0 };

export const useCompanyPagination = (pageSize = 10) => {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [networkId, setNetworkId] = useState<string | undefined>();
  const [companyGroupId, setCompanyGroupId] = useState<string | undefined>();
  const [data, setData] = useState<CompanyPage>(emptyPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursors, setCursors] = useState<Record<number, CompanyPageCursor | undefined>>({ 0: undefined });

  const cursor = cursors[page];

  const reset = useCallback((nextSearch = search, nextNetworkId = networkId) => {
    setPage(0);
    setCursors({ 0: undefined });
    setSearch(nextSearch);
    setNetworkId(nextNetworkId);
  }, [networkId, search]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await companiesService.listPage({
        pageSize,
        cursor,
        search: search.trim() || undefined,
        networkId,
        companyGroupId,
      });
      setData(result);

      const nextCursor = result.nextCursor;
      if (nextCursor) {
        setCursors((current) => {
          const known = current[page + 1];
          if (known?.id === nextCursor.id && known.nameFold === nextCursor.nameFold) return current;
          return { ...current, [page + 1]: nextCursor };
        });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [companyGroupId, cursor, networkId, page, pageSize, search]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    ...data,
    page,
    pageSize,
    search,
    networkId,
    companyGroupId,
    loading,
    error,
    refresh: load,
    next: () => data.nextCursor && setPage((current) => current + 1),
    previous: () => setPage((current) => Math.max(0, current - 1)),
    setSearch: (value: string) => reset(value, networkId),
    setNetworkId: (value?: string) => reset(search, value),
    setCompanyGroupId: (value?: string) => {
      setPage(0);
      setCursors({ 0: undefined });
      setCompanyGroupId(value);
    },
  };
};
